import { Intersection, Vector2 } from 'three';
import { Object3D } from 'three';
import { Vector3 } from 'three';

const _direction = new Vector3();

// Event pooling
// https://legacy.reactjs.org/docs/events.html
// https://github.com/mrdoob/three.js/blob/2ff77e4b335e31c108aac839a07401664998c730/src/core/Object3D.js#L28
// https://legacy.reactjs.org/docs/legacy-event-pooling.html
let _dragEvent: DragControlsEvent;

/**
 * DragControlsEvent state inspired by react-use-gesture
 * @see https://use-gesture.netlify.app/docs/state/#gesture-state-attributes
 */
export class DragControlsEvent {
  /**
   * The type of the drag event.
   */
  type: 'drag' | 'dragstart' | 'dragend' | 'dragcancel' = null!;
  /**
   * The phase of the drag event.
   */
  phase: 'start' | 'end' | 'move' = null!;
  /**
   * Whether the event is the first in the drag sequence.
   */
  first: boolean = false;
  /**
   * Whether the event is the last in the drag sequence.
   */
  last: boolean = false;
  /**
   * True when a mouse button or touch is down
   */
  down: boolean = false;
  /**
   *  Whether the drag event was canceled
   */
  signal: AbortSignal = null!;
  /**
   * The original pointer event.
   */
  event: PointerEvent = null!;
  /**
   * The object being dragged.
   */
  object: Object3D = null!;
  /**
   * The position of the pointer.
   */
  pointer: Vector2 = new Vector2();
  /**
   * The initial absolute position of the object.
   */
  initial: Vector3 = new Vector3();
  /**
   * The displacement between initial and current position.
   */
  movement: Vector3 = new Vector3();
  /**
   * The current absolute position of the object.
   */
  position: Vector3 = new Vector3();
  /**
   * The position when the last event started.
   */
  lastPosition: Vector3 = new Vector3();
  /**
   * movement delta (movement - previous movement)
   */
  delta: Vector3 = new Vector3();
  /**
   * The position distance per axis.
   */
  distance: Vector3 = new Vector3();
  /**
   * The direction of the drag event per axis.
   */
  direction = new Vector3();
  /**
   * True when shift key is pressed
   */
  shiftKey: boolean = false;
  /**
   * True when control key is pressed
   */
  ctrlKey: boolean = false;
  /**
   * True when alt key is pressed
   */
  altKey: boolean = false;
  /**
   * The drag event start time (ms)
   */
  startTime: number = 0;
  /**
   * The time stamp of the drag event (ms)
   */
  timeStamp: number = 0;
  /**
   * The time delta of the drag event (ms)
   */
  timeDelta: number = 0;
  /**
   * The elapsed time of the drag event (ms)
   */
  elapsedTime: number = 0;
  /**
   * Intersection data
   */
  intersection: Intersection = null!;
  /**
   * whether the drag event was canceled
   */
  get cancelled() {
    return this.signal.aborted;
  }
  /**
   * Persist the event for later use
   */
  persist = () => {
    _dragEvent = new DragControlsEvent();
    return this;
  };
  /**
   * Function you can call to interrupt the drag sequence.
   */
  cancel = (): this => {
    throw new Error('Method not implemented.');
  };
}

export type DragControlsEvents = {
  drag: DragControlsEvent;
  dragstart: DragControlsEvent;
  dragend: DragControlsEvent;
  dragcancel: DragControlsEvent;
};

_dragEvent = new DragControlsEvent();

const eventTypePhasesMapping = {
  dragstart: 'start',
  dragend: 'end',
  drag: 'move',
  dragcancel: 'end',
} as const;

export interface DragControlsEventOptions {
  type: 'drag' | 'dragstart' | 'dragend' | 'dragcancel';
  controller: AbortController;
  event: PointerEvent;
  intersection: Intersection;
  pointer: Vector2;
  initial: Vector3;
  position: Vector3;
  startTime: number;
  lastPosition?: Vector3;
  lastTimeStamp?: number;
}
/**
 * Create a drag event from pooled state.
 */
export const createDragControlsEvent = ({
  type,
  controller,
  event,
  intersection,
  pointer,
  initial,
  position,
  lastPosition,
  startTime,
  lastTimeStamp,
}: DragControlsEventOptions) => {
  _dragEvent.event = event;
  _dragEvent.object = intersection.object;
  _dragEvent.intersection = intersection;
  _dragEvent.signal = controller.signal;
  _dragEvent.cancel = function () {
    controller.abort();
    return this;
  };
  // State
  _dragEvent.type = type;
  _dragEvent.phase = eventTypePhasesMapping[type];
  _dragEvent.first = type === 'dragstart';
  _dragEvent.last = type === 'dragend' || type === 'dragcancel';
  _dragEvent.down = type === 'dragstart' || type === 'drag';
  // Position
  _dragEvent.pointer.copy(pointer);
  _dragEvent.initial.copy(initial);
  applyPosition(_dragEvent, position);
  _dragEvent.lastPosition.copy(lastPosition ?? _dragEvent.position);
  // Key modifiers
  _dragEvent.shiftKey = event.shiftKey;
  _dragEvent.ctrlKey = event.ctrlKey;
  _dragEvent.altKey = event.altKey;
  // Time
  _dragEvent.timeStamp = event.timeStamp;
  _dragEvent.startTime = startTime;
  _dragEvent.timeDelta = lastTimeStamp ? event.timeStamp - lastTimeStamp : 0;
  _dragEvent.elapsedTime = event.timeStamp - startTime;
  return _dragEvent;
};

export const applyPosition = (evt: DragControlsEvent, position: Vector3) => {
  _dragEvent.position.copy(position);
  _dragEvent.movement.copy(position).sub(_dragEvent.initial);
  _direction.copy(_dragEvent.movement).normalize();
  _dragEvent.direction.copy(_direction);
  _dragEvent.distance.copy(_direction.multiplyScalar(_dragEvent.movement.length()));
  _dragEvent.delta.copy(_dragEvent.position).sub(_dragEvent.lastPosition);
};
